import React from 'react';
import { CheckCircle, Copy, Download, Printer, Sliders, Zap } from 'lucide-react';
import { Switch } from '../../ui/switch';

export default function ImpressionTab({ PREVIEW_FONT_PX, copyTokenToClipboard, debounceInput, downloadingConfig, generatingToken, handleDownloadConfig, handleGenerateToken, handleSaveDebounce, handleSavePrintFormat, handleToggleImpression, impressionSettings, previewStyle, printFormat, savingDebounce, savingPrintFormat, setDebounceInput, setPrintFormat, togglePrintFormat, tokenCopied }) {
  return (
        <div className="glass-card">
          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">Impressão Automática de Pedidos</h2>
            <p className="text-gray-400 text-sm">
              Configure a impressão automática para receber pedidos diretamente na sua impressora térmica
            </p>
          </div>

          {/* Status Card */}
          <div className="mb-6 p-5 bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-white/10 rounded-xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${
                  impressionSettings.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  <Printer className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-semibold text-lg">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      impressionSettings.active 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                    }`}>
                      {impressionSettings.active ? '✓ Ativo' : '○ Inativo'}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    {impressionSettings.configured 
                      ? 'Token configurado e pronto para uso' 
                      : 'Configure um token para começar'}
                  </p>
                </div>
              </div>
              {impressionSettings.configured && (
                <Switch
                  id="impression-active"
                  checked={impressionSettings.active}
                  onCheckedChange={handleToggleImpression}
                  className="scale-125"
                  data-testid="impression-toggle"
                />
              )}
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <button
              onClick={handleGenerateToken}
              disabled={generatingToken}
              className="flex items-center justify-center space-x-2 p-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="generate-token-button"
            >
              <Zap className="w-5 h-5" />
              <span>{generatingToken ? 'Gerando...' : impressionSettings.configured ? 'Gerar Novo Token' : 'Gerar Token de Impressão'}</span>
            </button>

            <button
              onClick={handleDownloadConfig}
              disabled={!impressionSettings.configured || downloadingConfig}
              className="flex items-center justify-center space-x-2 p-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="download-config-button"
            >
              <Download className="w-5 h-5" />
              <span>{downloadingConfig ? 'Baixando...' : 'Baixar login.json'}</span>
            </button>

            <a
              href="/PedeFacil-Impressora.zip"
              download
              className="flex items-center justify-center space-x-2 p-4 bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white rounded-xl font-semibold transition-all duration-200"
              data-testid="download-exe-button"
            >
              <Download className="w-5 h-5" />
              <span>Baixar Programa (.exe)</span>
            </a>
          </div>

          {/* Token Display */}
          {impressionSettings.token && (
            <div className="mb-8 p-5 bg-white/5 border border-white/10 rounded-xl">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Token de Impressão
              </label>
              <div className="flex items-center space-x-2 bg-gray-900/50 border border-white/10 rounded-lg p-3">
                <code className="flex-1 text-white font-mono text-sm break-all">
                  {impressionSettings.token.substring(0, 12)}...{impressionSettings.token.substring(impressionSettings.token.length - 6)}
                </code>
                <button
                  onClick={copyTokenToClipboard}
                  className="flex-shrink-0 p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Copiar token"
                  data-testid="copy-token-button"
                >
                  {tokenCopied ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <Copy className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Debounce de impressão de mesa */}
          {impressionSettings.configured && (
            <div className="mb-8 p-5 bg-white/5 border border-white/10 rounded-xl" data-testid="print-debounce-section">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <label className="block text-sm font-semibold text-white mb-1">
                    Tempo de espera para impressão de mesa
                  </label>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Quando o garçom adicionar itens em sequência, o sistema aguarda esse tempo após o último item para enviar um único comprovante à cozinha.
                    Itens adicionados depois saem em um comprovante marcado &quot;ADICIONAL&quot;.
                    <br />
                    <span className="text-gray-500">Delivery e balcão continuam imprimindo imediato. 0 = desativa o debounce.</span>
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex items-center bg-gray-900/50 border border-white/10 rounded-lg overflow-hidden">
                  <input
                    type="number"
                    min={0}
                    max={300}
                    step={5}
                    value={debounceInput}
                    onChange={(e) => setDebounceInput(e.target.value)}
                    className="flex-1 sm:w-32 bg-transparent text-white font-mono text-lg px-4 py-2 focus:outline-none"
                    data-testid="print-debounce-input"
                  />
                  <span className="px-3 text-gray-400 text-sm border-l border-white/10 py-2">segundos</span>
                </div>
                <button
                  onClick={handleSaveDebounce}
                  disabled={savingDebounce || Number(debounceInput) === Number(impressionSettings.print_debounce_seconds)}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-print-debounce-button"
                >
                  {savingDebounce ? 'Salvando...' : 'Salvar'}
                </button>
                <div className="text-xs text-gray-500 sm:ml-2">
                  Padrão recomendado: <span className="text-gray-300 font-semibold">70s</span>
                </div>
              </div>
            </div>
          )}

          {/* Personalização do Comprovante */}
          {impressionSettings.configured && (
            <div className="mb-8 p-5 bg-white/5 border border-white/10 rounded-xl" data-testid="print-format-section">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-purple-400" />
                  Personalização do Comprovante
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed mt-1">
                  Escolha o que aparece no cupom, deixe partes em negrito e defina a mensagem de rodapé.
                  Vale para a impressão automática (térmica) e a manual (pelo painel).
                </p>
              </div>

              {/* Seletor de MODO de impressão */}
              <div className="mb-6 p-4 bg-gray-900/40 border border-white/10 rounded-xl" data-testid="print-mode-section">
                <p className="text-sm font-semibold text-gray-200 mb-1">Modo de impressão</p>
                <p className="text-xs text-gray-400 mb-3 leading-relaxed">
                  Use <b>Tradicional</b> se imprime pelo app Android (Pede é Fácil no celular). Use <b>Personalizado</b> se imprime pelo PC/executável Windows — libera o controle de tamanho da fonte.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { val: 'tradicional', label: 'Tradicional (App Android)', desc: 'Cupom padrão, compatível com o app' },
                    { val: 'personalizado', label: 'Personalizado (PC)', desc: 'Fonte maior e mais legível' },
                  ].map(({ val, label, desc }) => (
                    <button key={val} type="button"
                      onClick={() => setPrintFormat(prev => ({ ...prev, print_mode: val }))}
                      className={`text-left p-3 rounded-lg border transition-all ${printFormat.print_mode === val ? 'border-purple-500 bg-purple-500/15' : 'border-white/10 bg-gray-900/40 hover:border-white/20'}`}
                      data-testid={`print-mode-${val}`}>
                      <p className={`text-sm font-semibold ${printFormat.print_mode === val ? 'text-purple-300' : 'text-white'}`}>{label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              {printFormat.print_mode === 'personalizado' ? (
                <div className="mb-6" data-testid="print-size-section">
                  <p className="text-sm font-semibold text-gray-300 mb-1">Tamanho da fonte</p>
                  <p className="text-xs text-gray-400 mb-3">Válido apenas para impressoras térmicas via PC (ESC/POS).</p>
                  <div className="space-y-3">
                    {[
                      { key: 'font_size', label: 'Corpo geral', opts: [['normal', 'Normal'], ['media', 'Média'], ['grande', 'Grande']] },
                      { key: 'size_order_number', label: 'Número do pedido', opts: [['normal', 'Normal'], ['grande', 'Grande'], ['gigante', 'Gigante']] },
                      { key: 'size_total', label: 'TOTAL', opts: [['normal', 'Normal'], ['grande', 'Grande'], ['gigante', 'Gigante']] },
                      { key: 'size_customer_name', label: 'Nome do cliente', opts: [['normal', 'Normal'], ['grande', 'Grande']] },
                      { key: 'size_items', label: 'Itens', opts: [['normal', 'Normal'], ['media', 'Maior']] },
                    ].map(({ key, label, opts }) => (
                      <div key={key} className="flex items-center justify-between gap-3 p-3 bg-gray-900/40 border border-white/10 rounded-xl">
                        <p className="text-sm font-medium text-white">{label}</p>
                        <div className="flex gap-1 flex-wrap justify-end">
                          {opts.map(([val, lbl]) => (
                            <button key={val} type="button"
                              onClick={() => setPrintFormat(prev => ({ ...prev, [key]: val }))}
                              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${printFormat[key] === val ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                              data-testid={`size-${key}-${val}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm font-semibold text-gray-300 mt-5 mb-2">Pré-visualização</p>
                  <div className="bg-white text-black rounded-lg p-4 mx-auto"
                    style={{ fontFamily: 'monospace', maxWidth: '320px', fontSize: (PREVIEW_FONT_PX[printFormat.font_size] || 11) + 'px' }}
                    data-testid="print-preview">
                    <div className="text-center" style={previewStyle('size_order_number', 'bold_order_number')}>PEDIDO #42</div>
                    {printFormat.header_text ? <div className="text-center">{printFormat.header_text}</div> : null}
                    <div className="border-t border-dashed border-gray-400 my-2"></div>
                    <div style={previewStyle('size_customer_name', 'bold_customer_name')}>Cliente: João Silva</div>
                    {printFormat.show_datetime ? <div>Data/Hora: 22/08/2026, 12:00</div> : null}
                    {printFormat.show_phone ? <div>Telefone: (11) 99999-9999</div> : null}
                    <div className="border-t border-dashed border-gray-400 my-2"></div>
                    <div style={previewStyle('size_items', 'bold_product_name')}>2x Hambúrguer  R$ 50.00</div>
                    <div style={previewStyle('size_items', 'bold_product_name')}>1x Refrigerante  R$ 8.00</div>
                    <div className="border-t border-dashed border-gray-400 my-2"></div>
                    {printFormat.show_subtotal ? <div>Subtotal: R$ 58.00</div> : null}
                    <div style={previewStyle('size_total', 'bold_total')}>TOTAL: R$ 63.00</div>
                    {printFormat.show_payment_method ? <div>Pagamento: PIX</div> : null}
                    {printFormat.show_footer ? (
                      <>
                        <div className="border-t border-dashed border-gray-400 my-2"></div>
                        <div className="text-center">{printFormat.footer_message || 'Obrigado pela preferencia!'}</div>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-gray-900/30 border border-dashed border-white/10 rounded-xl" data-testid="print-size-locked">
                  <p className="text-xs text-gray-400 leading-relaxed">
                    🔒 O controle de <b>tamanho da fonte</b> fica disponível no modo <b>Personalizado (PC)</b>. No modo Tradicional o cupom mantém o formato padrão, compatível com o app Android.
                  </p>
                </div>
              )}
              <p className="text-sm font-semibold text-gray-300 mb-3">Negrito (destaque)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {[
                  { key: 'bold_additionals', label: 'Adicionais', sub: 'Resolve adicionais "quase invisíveis"' },
                  { key: 'bold_product_name', label: 'Nome dos produtos' },
                  { key: 'bold_total', label: 'Total do pedido' },
                  { key: 'bold_order_number', label: 'Número do pedido' },
                  { key: 'bold_customer_name', label: 'Nome do cliente' },
                  { key: 'bold_observations', label: 'Observações' },
                ].map(({ key, label, sub }) => (
                  <div key={key} className="flex items-center justify-between gap-3 p-4 bg-gray-900/40 border border-white/10 rounded-xl" data-testid={`toggle-${key}`}>
                    <div>
                      <p className="text-sm font-medium text-white">{label}</p>
                      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
                    </div>
                    <Switch checked={printFormat[key]} onCheckedChange={() => togglePrintFormat(key)} className="scale-125" />
                  </div>
                ))}
              </div>

              <p className="text-sm font-semibold text-gray-300 mb-3">Campos exibidos no cupom</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {[
                  { key: 'show_datetime', label: 'Data e hora' },
                  { key: 'show_phone', label: 'Telefone do cliente' },
                  { key: 'show_address', label: 'Endereço de entrega' },
                  { key: 'show_delivery_fee', label: 'Taxa de entrega' },
                  { key: 'show_subtotal', label: 'Subtotal' },
                  { key: 'show_payment_method', label: 'Forma de pagamento' },
                  { key: 'show_observations', label: 'Observações dos itens' },
                  { key: 'show_footer', label: 'Rodapé (mensagem)' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between gap-3 p-4 bg-gray-900/40 border border-white/10 rounded-xl" data-testid={`toggle-${key}`}>
                    <p className="text-sm font-medium text-white">{label}</p>
                    <Switch checked={printFormat[key]} onCheckedChange={() => togglePrintFormat(key)} className="scale-125" />
                  </div>
                ))}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Linha extra no topo <span className="text-gray-500 font-normal">(opcional — ex: nome da loja)</span>
                </label>
                <input
                  type="text" maxLength={80}
                  value={printFormat.header_text}
                  onChange={(e) => setPrintFormat(prev => ({ ...prev, header_text: e.target.value }))}
                  placeholder="Ex: Pizzaria do Zé"
                  className="w-full bg-gray-900/50 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  data-testid="print-format-header-input"
                />
              </div>
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-300 mb-1">Mensagem de rodapé</label>
                <input
                  type="text" maxLength={120}
                  value={printFormat.footer_message}
                  onChange={(e) => setPrintFormat(prev => ({ ...prev, footer_message: e.target.value }))}
                  placeholder="Ex: Obrigado pela preferência! Volte sempre :)"
                  className="w-full bg-gray-900/50 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  data-testid="print-format-footer-input"
                />
              </div>

              <div className="flex items-center justify-between gap-3 p-4 bg-gray-900/40 border border-white/10 rounded-xl mb-5" data-testid="toggle-escpos_enabled">
                <div>
                  <p className="text-sm font-medium text-white">Negrito real na impressora térmica (ESC/POS)</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                    Mantenha ligado para negrito de verdade na maioria das impressoras térmicas. Se o seu cupom sair com símbolos estranhos, desligue esta opção.
                  </p>
                </div>
                <Switch checked={printFormat.escpos_enabled} onCheckedChange={() => togglePrintFormat('escpos_enabled')} className="scale-125" />
              </div>

              <button
                onClick={handleSavePrintFormat}
                disabled={savingPrintFormat}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-print-format-button"
              >
                {savingPrintFormat ? 'Salvando...' : 'Salvar personalização'}
              </button>
            </div>
          )}

          {/* Instruções de Uso */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Como Configurar a Impressão Automática</h3>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-500/20 text-blue-400 rounded-full font-bold">
                  1
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Gere o Token de Impressão</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Clique no botão &quot;Gerar Token de Impressão&quot; acima para criar um token único para seu restaurante
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-green-500/20 text-green-400 rounded-full font-bold">
                  2
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Baixe o Arquivo de Configuração</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Clique em &quot;Baixar login.json&quot; para obter o arquivo de configuração
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-purple-500/20 text-purple-400 rounded-full font-bold">
                  3
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Configure o Executável</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Coloque o arquivo login.json na mesma pasta do executável de impressão (.exe)
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-orange-500/20 text-orange-400 rounded-full font-bold">
                  4
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Execute e Ative</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Execute o programa de impressão e ative a impressão automática usando o toggle acima
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Informações Importantes */}
          <div className="mt-8 p-5 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
            <div className="flex items-start space-x-3">
              <div className="text-yellow-400 text-xl mt-0.5">⚠️</div>
              <div className="flex-1">
                <p className="text-yellow-400 text-sm font-medium mb-1">Informações Importantes</p>
                <ul className="text-gray-300 text-sm space-y-1 leading-relaxed">
                  <li>• O executável verifica novos pedidos a cada 5 segundos</li>
                  <li>• Apenas pedidos com status &quot;Preparando&quot; são impressos automaticamente</li>
                  <li>• Após impressão, o pedido é marcado como &quot;Impresso&quot; no sistema</li>
                  <li>• Mantenha o executável rodando enquanto quiser receber impressões automáticas</li>
                  <li>• Certifique-se de que sua impressora térmica está conectada e configurada</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
  );
}
